import { useState, useEffect } from "react";
import { Box, Button } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import GenericFormPopup from "../GenericFormPopup/GenericFormPopup";
import StatusSnackbar from "../../General/StatusSnackbar/StatusSnackbar";
import useAuth from "../../../hooks/useAuth";
import { apiVariable } from "../../../api/apiVariable";
import {
  loanFieldConfiguration,
  loanInitialValues,
} from "../../../formConfigs/Fleet/loanFieldConfig";
import { getId } from "../../HR/UpdateEmployeePopup/UpdateEmployeePopup";
import { fetchLenders } from "../LoanPopup/LoanPopup";
import dayjs from "dayjs";
import LenderPopup from "../LenderPopup/LenderPopup";

interface UpdateLoanPopupProps {
  loan: any;
}

const UpdateLoanPopup = ({ loan }: UpdateLoanPopupProps) => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [initialValues, setInitialValues] = useState(loanInitialValues);
  const queryClient = useQueryClient();
  const { token } = useAuth();

  const { data: lendersData } = useQuery({
    queryKey: ["lenders"],
    queryFn: () => fetchLenders(token),
    enabled: !!token && open,
  });

  useEffect(() => {
    if (loan) {
      setInitialValues({
        ...loanInitialValues,
        ...loan,
        lenderId:
          lendersData?.find((l: any) => l.id === loan.lenderId) ||
          loan.lenderId,
        loanStartDate: dayjs(loan.loanStartDate).format("YYYY-MM-DD"),
        loanEndDate: dayjs(loan.loanEndDate).format("YYYY-MM-DD"),
      });
    }
  }, [loan, lendersData]);

  const updateMutation = useMutation({
    mutationFn: (data: any) =>
      axios.put(`${apiVariable}/loan`, data, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    onSuccess: () => {
      setStatus("success");
      queryClient.invalidateQueries({ queryKey: ["loans"] });
      setOpen(false);
    },
    onError: (error: any) => {
      setStatus("error");
      setErrorMessage(error.response?.data || "Failed to update loan");
    },
  });

  const submitHandler = (data: any) => {
    const submitData = {
      ...data,
      lenderId: getId(data.lenderId),
    };
    updateMutation.mutate(submitData);
  };

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
      >
        Update
      </Button>
      <GenericFormPopup
        formFields={loanFieldConfiguration}
        initialValues={initialValues}
        title="Update Loan"
        onSubmit={submitHandler}
        open={open}
        onClose={() => setOpen(false)}
        autocompleteData={{ lenders: lendersData }}
        loading={updateMutation.isPending}
        fieldComponents={{
          lenderId: (
            <Box display="flex" justifyContent="center" alignItems="center">
              <LenderPopup />
            </Box>
          ),
        }}
      />
      <StatusSnackbar
        status={status}
        successMessage="Loan updated successfully"
        errorMessage={errorMessage}
        handleClose={() => setStatus("idle")}
      />
    </>
  );
};

export default UpdateLoanPopup;
